import { memo, useMemo, type FC } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { useTheme } from "@/hooks/use-theme";

export type CursorZone =
    | "top-left"
    | "top"
    | "top-right"
    | "left"
    | "center"
    | "right"
    | "bottom-left"
    | "bottom"
    | "bottom-right"
    | null;

/**
 * Gradient background with grain texture.
 */
export const GradientBackground: FC<{ className?: string }> = ({ className }) => {
    return (
        <div className={className}>
            {/* SVG filter for grain texture */}
            <svg className="hidden" aria-hidden="true">
                <filter id="grain">
                    <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="4" stitchTiles="stitch" />
                    <feColorMatrix type="saturate" values="0" />
                </filter>
            </svg>

            <div className="absolute inset-0 bg-white dark:bg-surface-base" />

            <div
                className="pointer-events-none absolute inset-0 opacity-[0.06]"
                style={{ filter: "url(#grain)" }}
            />
        </div>
    );
};

type BlobConfig = {
    width: number;
    height: number;
    position: { left?: string; right?: string; top?: string; bottom?: string };
    color: string;
    darkColor: string;
    zones: CursorZone[];
    baseTransform?: string;
    duration?: number; // Animation duration in ms (default: 700)
};

// Blob colors are intentionally hardcoded as one-off values for this decorative aura effect.
// They are not part of the design system tokens since they're specific to this visual treatment.

// Light mode colors
const BLUE_COLOR_1 = "rgba(147, 157, 255, 0.75)";
const BLUE_COLOR_2 = "rgba(165, 180, 252, 0.65)";
const BLUE_COLOR_3 = "rgba(147, 157, 255, 0.55)";
const BLUE_COLOR_4 = "rgba(147, 157, 255, 0.7)";
const BLUE_COLOR_5 = "rgba(165, 180, 252, 0.5)";
const ORANGE_COLOR_1 = "rgba(255, 165, 70, 0.8)";
const ORANGE_COLOR_2 = "rgba(255, 177, 101, 0.75)";
const ORANGE_COLOR_3 = "rgba(255, 190, 120, 0.55)";

// Dark mode colors - darker base colors with moderate opacity
const BLUE_COLOR_1_DARK = "rgba(80, 90, 160, 0.45)";
const BLUE_COLOR_2_DARK = "rgba(90, 100, 170, 0.4)";
const BLUE_COLOR_3_DARK = "rgba(80, 90, 160, 0.35)";
const BLUE_COLOR_4_DARK = "rgba(80, 90, 160, 0.42)";
const BLUE_COLOR_5_DARK = "rgba(90, 100, 170, 0.3)";
const ORANGE_COLOR_1_DARK = "rgba(180, 100, 40, 0.5)";
const ORANGE_COLOR_2_DARK = "rgba(180, 110, 50, 0.45)";
const ORANGE_COLOR_3_DARK = "rgba(180, 120, 60, 0.35)";

const BLOB_CONFIGS: BlobConfig[] = [
    // Blue blob: Top-left corner
    {
        width: 300,
        height: 300,
        position: { left: "0", top: "20px" },
        color: BLUE_COLOR_1,
        darkColor: BLUE_COLOR_1_DARK,
        zones: ["top-left", "top", "left"],
        duration: 600,
    },
    // Blue blob: Top edge (moved down 25%)
    {
        width: 240,
        height: 240,
        position: { left: "50%", top: "25%" },
        color: BLUE_COLOR_2,
        darkColor: BLUE_COLOR_2_DARK,
        zones: ["top", "top-left", "top-right", "center"],
        baseTransform: "translateX(-50%)",
        duration: 800,
    },
    // Blue blob: Top-right (closer to modal)
    {
        width: 180,
        height: 180,
        position: { right: "0", top: "15%" },
        color: BLUE_COLOR_3,
        darkColor: BLUE_COLOR_3_DARK,
        zones: ["top-right", "top", "right"],
        duration: 550,
    },
    // Blue blob: Right-lower
    {
        width: 200,
        height: 200,
        position: { right: "-10px", bottom: "15%" },
        color: BLUE_COLOR_4,
        darkColor: BLUE_COLOR_4_DARK,
        zones: ["right", "bottom-right"],
        duration: 750,
    },
    // Orange blob: Right edge (reduced from 350px)
    {
        width: 290,
        height: 290,
        position: { right: "-20px", top: "50%" },
        color: ORANGE_COLOR_1,
        darkColor: ORANGE_COLOR_1_DARK,
        zones: ["right", "top-right", "bottom-right", "center"],
        baseTransform: "translateY(-50%)",
        duration: 900,
    },
    // Blue blob: Left center-top (connects with top-left blue)
    {
        width: 160,
        height: 160,
        position: { left: "0", top: "30%" },
        color: BLUE_COLOR_5,
        darkColor: BLUE_COLOR_5_DARK,
        zones: ["left", "top-left", "center"],
        duration: 650,
    },
    // Orange blob: Bottom-left corner (moved in slightly)
    {
        width: 280,
        height: 280,
        position: { left: "40px", bottom: "20px" },
        color: ORANGE_COLOR_2,
        darkColor: ORANGE_COLOR_2_DARK,
        zones: ["bottom-left", "bottom", "left"],
        duration: 700,
    },
    // Orange blob: Bottom edge
    {
        width: 200,
        height: 200,
        position: { left: "55%", bottom: "10px" },
        color: ORANGE_COLOR_3,
        darkColor: ORANGE_COLOR_3_DARK,
        zones: ["bottom", "bottom-left", "bottom-right", "center"],
        duration: 850,
    },
];

type BlobProps = {
    config: BlobConfig;
    isActive: boolean;
    isDark: boolean;
};

const Blob = memo<BlobProps>(({ config, isActive, isDark }) => {
    const { width, height, position, color, darkColor, baseTransform, duration = 700 } = config;

    const transform = useMemo(() => {
        const scale = isActive ? "scale(1.15)" : "scale(1)";
        return baseTransform ? `${baseTransform} ${scale}` : scale;
    }, [isActive, baseTransform]);

    const activeColor = isDark ? darkColor : color;

    // In dark mode, keep opacity constant (no hover change)
    const opacityClass = isDark ? "opacity-100" : isActive ? "opacity-100" : "opacity-80";

    return (
        <div
            className={cn(
                "absolute rounded-full blur-3xl will-change-transform",
                "transition-[transform,opacity] ease-in-out",
                opacityClass,
            )}
            style={{
                width: `${width}px`,
                height: `${height}px`,
                ...position,
                transform,
                background: `radial-gradient(circle, ${activeColor} 0%, transparent 70%)`,
                transitionDuration: `${duration}ms`,
            }}
        />
    );
});

Blob.displayName = "Blob";

/**
 * Aura blobs that wrap around the modal.
 * Individual blobs grow based on cursor position within the modal.
 */
export const ModalAura: FC<{ className?: string; cursorZone?: CursorZone }> = memo(({ className, cursorZone }) => {
    const { effectiveTheme } = useTheme();
    const isDark = effectiveTheme === "dark";

    return (
        <div className={cn("pointer-events-none absolute -inset-40", className)} aria-hidden="true">
            {BLOB_CONFIGS.map((config, index) => (
                <Blob
                    key={index}
                    config={config}
                    isActive={config.zones.includes(cursorZone ?? null)}
                    isDark={isDark}
                />
            ))}
        </div>
    );
});

ModalAura.displayName = "ModalAura";
